/**
 * i18next 配置
 *
 * 详见 docs/specs/architecture.md §3.6 i18n 规范
 *
 * - 5 语言: en / ja / ko / de / zh-TW（OPT-005，简中已下线）
 * - 默认语言 = DEFAULT_LANGUAGE（shared/i18n/config.ts 单一配置点，当前繁中）
 * - localStorage 持久化 (key: 'course-management-web-lang')
 * - 字典从 shared/i18n/{lang}.json 加载 (同 workspace package)
 */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import en from '../../../shared/i18n/en.json'
import ja from '../../../shared/i18n/ja.json'
import ko from '../../../shared/i18n/ko.json'
import de from '../../../shared/i18n/de.json'
import zhTW from '../../../shared/i18n/zh-TW.json'
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, normalizeLanguage } from '../../../shared/i18n/config'

export { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, normalizeLanguage }
export type { SupportedLanguage } from '../../../shared/i18n/config'

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ja: { translation: ja },
      ko: { translation: ko },
      de: { translation: de },
      'zh-TW': { translation: zhTW },
    },
    // 默认与回退都指向单一配置点；fallback 链尾部兜底 en
    lng: DEFAULT_LANGUAGE,
    fallbackLng: { default: [DEFAULT_LANGUAGE, 'en'] },
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // 检测出的 zh-CN/zh/en-US 等统一归一（简中存量偏好 → 繁中）
    load: 'currentOnly',
    debug: false,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'course-management-web-lang',
      caches: ['localStorage'],
      convertDetectedLanguage: (lng: string) => normalizeLanguage(lng),
    },
  })

// 显式 changeLanguage 也统一归一（如已下线的 zh-CN → zh-TW），
// 避免绕过 detection 的调用方把实例切到无资源的语言码
const rawChangeLanguage = i18n.changeLanguage.bind(i18n)
i18n.changeLanguage = ((lng?: string, callback?: (err: unknown, t: typeof i18n.t) => void) =>
  rawChangeLanguage(lng ? normalizeLanguage(lng) : lng, callback)) as typeof i18n.changeLanguage

export default i18n
